import type { TaxonomyClass, UnknownLabel } from "@sitescene/types";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiClient } from "@/api/client";
import { ENDPOINTS } from "@/api/endpoints";
import { queryKeys } from "@/api/query-keys";

/** Labels the taxonomy could not place, most frequent first. Global, not org-scoped. */
export const useUnknownLabels = (status = "pending") =>
	useQuery({
		queryFn: async () => {
			const response = await apiClient.get<UnknownLabel[]>(
				ENDPOINTS.shadow.plausibility.unknownLabels,
				{ params: { limit: 100, status } },
			);
			return response.data;
		},
		queryKey: queryKeys.plausibility.unknownLabels(status),
	});

export interface AddAliasInput {
	alias: string;
	classId: string;
	unknownLabelIds: string[];
}

/** Add an alias to a class and resolve the queue rows it explains. */
export const useAddAlias = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (input: AddAliasInput) => {
			const response = await apiClient.post<TaxonomyClass>(
				ENDPOINTS.shadow.plausibility.aliases,
				input,
			);
			return response.data;
		},
		onSuccess: async () => {
			await Promise.all([
				queryClient.invalidateQueries({
					queryKey: ["shadow", "plausibility", "unknown-labels"],
				}),
				queryClient.invalidateQueries({
					queryKey: queryKeys.plausibility.taxonomy,
				}),
			]);
		},
	});
};

export const useIgnoreUnknownLabels = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: async (unknownLabelIds: string[]) => {
			const response = await apiClient.post<{ ignored: number }>(
				ENDPOINTS.shadow.plausibility.unknownLabelsIgnore,
				{ unknownLabelIds },
			);
			return response.data;
		},
		onSuccess: async () => {
			await queryClient.invalidateQueries({
				queryKey: ["shadow", "plausibility", "unknown-labels"],
			});
		},
	});
};
